import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Input, List, Typography, Tag } from 'antd';
import type { InputRef } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { getRouteSearchIndex } from '../../routes';

const { Text } = Typography;

const RECENTS_STORAGE_KEY = 'global-command-palette:recents:v1';
const MAX_RECENTS = 12;

function normalize(s: string) {
  return s
    .toLowerCase()
    .normalize('NFKD')
    // remove diacritics
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function safeReadRecents(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x) => typeof x === 'string');
  } catch {
    return [];
  }
}

function safeWriteRecents(paths: string[]) {
  try {
    window.localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(paths));
  } catch {
    // ignore storage failures (private mode, quota, etc.)
  }
}

const GlobalCommandPalette: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<InputRef | null>(null);
  const [recentPaths, setRecentPaths] = useState<string[]>([]);

  const allItems = useMemo(() => getRouteSearchIndex(), [i18n.language]);
  const normalizedQuery = useMemo(() => normalize(query), [query]);

  // Track recent routes (non-destructive, UI-independent)
  useEffect(() => {
    const existing = safeReadRecents();
    setRecentPaths(existing);
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (!path || path === '/login' || path === '/' || path === '*') return;
    setRecentPaths((prev) => {
      const next = [path, ...prev.filter((p) => p !== path)].slice(0, MAX_RECENTS);
      safeWriteRecents(next);
      return next;
    });
  }, [location.pathname]);

  const filtered = useMemo(() => {
    const q = normalizedQuery;
    const base = allItems.filter((it) => it.path !== location.pathname);
    if (!q) return base.slice(0, 20);

    const scored = base
      .map((it, index) => {
        const hay = normalize(`${it.label} ${it.sectionLabel ?? ''} ${it.path}`);
        let score = 0;
        if (hay.startsWith(q)) score += 5;
        if (normalize(it.label).startsWith(q)) score += 5;
        if (hay.includes(q)) score += 2;
        if (normalize(it.path).includes(q)) score += 1;
        return { it, score, index };
      })
      .filter((x) => x.score > 0)
      // Sort by score but preserve original route order as tie-breaker
      .sort((a, b) => b.score - a.score || a.index - b.index);

    return scored.map((x) => x.it).slice(0, 30);
  }, [allItems, location.pathname, normalizedQuery]);

  const recentItems = useMemo(() => {
    const lookup = new Map(allItems.map((it) => [it.path, it]));
    const items = recentPaths
      .filter((p) => p !== location.pathname && p !== '/login' && p !== '/' && p !== '*')
      .map((p) => lookup.get(p) ?? { path: p, label: p });
    return items.slice(0, MAX_RECENTS);
  }, [allItems, location.pathname, recentPaths]);

  const displayedItems = normalizedQuery ? filtered : recentItems;

  const close = () => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  };

  const openPalette = () => {
    // Avoid opening on login screen (no header context)
    if (location.pathname === '/login') return;
    setOpen(true);
    setQuery('');
    setActiveIndex(0);
  };

  useKeyboardShortcuts(
    [
      { key: 'k', ctrl: true, handler: openPalette },
      { key: 'k', meta: true, handler: openPalette },
    ],
    ['input', 'textarea', '[contenteditable="true"]']
  );

  useEffect(() => {
    if (!open) return;
    // Focus search input after modal opens
    const t = window.setTimeout(() => {
      inputRef.current?.focus?.();
      inputRef.current?.select?.();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    // Keep active index within bounds
    if (activeIndex < 0) setActiveIndex(0);
    if (activeIndex >= displayedItems.length) setActiveIndex(Math.max(0, displayedItems.length - 1));
  }, [activeIndex, filtered.length]);

  const goTo = (path: string) => {
    close();
    navigate(path);
  };

  return (
    <Modal
      title="Search"
      open={open}
      onCancel={close}
      footer={null}
      destroyOnClose
      width={720}
    >
      <div onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveIndex((i) => Math.max(0, i - 1));
          return;
        }
        if (e.key === 'Enter') {
          const item = displayedItems[activeIndex];
          if (item?.path) {
            e.preventDefault();
            goTo(item.path);
          }
        }
      }}>
        <Input
          ref={(r) => { inputRef.current = r; }}
          placeholder="Search pages, modules, or paths..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          allowClear
        />

        <div style={{ marginTop: 12 }}>
          {!normalizedQuery && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Recent pages
            </Text>
          )}
          <List
            size="small"
            bordered
            dataSource={displayedItems}
            locale={{ emptyText: normalizedQuery ? 'No matches' : 'No recent pages' }}
            renderItem={(item, idx) => {
              const active = idx === activeIndex;
              return (
                <List.Item
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => goTo(item.path)}
                  style={{
                    cursor: 'pointer',
                    background: active ? 'rgba(22, 119, 255, 0.08)' : undefined,
                  }}
                >
                  <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'center' }}>
                    <div style={{ width: 18, display: 'flex', justifyContent: 'center' }}>
                      {item.icon ?? null}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Text strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.label}
                        </Text>
                        {item.sectionLabel ? <Tag style={{ marginInlineStart: 'auto' }}>{item.sectionLabel}</Tag> : null}
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {item.path}
                      </Text>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        </div>

        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Tip: Ctrl+K (or Cmd+K)
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            ↑ ↓ to navigate, Enter to open
          </Text>
        </div>
      </div>
    </Modal>
  );
};

export default GlobalCommandPalette;

